import React, { useMemo } from 'react'
import cx from 'classnames'
import { pathOr } from 'ramda'
import { subscribe } from 'react-contextual'
import { SearchFilterProvider } from 'views/providers'
import PortfoliosProvider from '../../PortfoliosProvider'
import { TFilterableLease, applyLeaseFilterPills, countLeasesForPill, getLeaseFilterGroups } from '../../filters'
import { useLeaseFilterPills } from '../../useLeaseFilterPills'
import styles from './LeaseFilterPills.module.scss'

type TProps = {
  filters: Array<{ key: string; portfolios: TFilterableLease[] }>
  activeFilterIndex: number
}

const LeaseFilterPills = ({ filters, activeFilterIndex }: TProps): React.ReactElement => {
  const { selection, togglePill } = useLeaseFilterPills()

  const leases = pathOr<TFilterableLease[]>([], [activeFilterIndex, 'portfolios'], filters)
  const tabKey = pathOr<string>('', [activeFilterIndex, 'key'], filters)

  const groups = useMemo(
    () =>
      getLeaseFilterGroups(tabKey).map(group => {
        // Counts are faceted: a pill is counted against the leases left once every *other*
        // group's selection is applied, so picking "Managed" narrows the Expiring in counts and
        // vice versa. The group's own selection is deliberately excluded, so each pill reads as
        // "what I'd get if I switched to this one" rather than 0.
        const countableLeases = applyLeaseFilterPills(leases, selection, { tabKey, excludeGroupKey: group.key })

        return {
          ...group,
          pills: group.pills.map(pill => ({
            ...pill,
            count: countLeasesForPill(countableLeases, pill),
            isActive: selection[group.key] === pill.key,
          })),
        }
      }),
    [leases, selection, tabKey],
  )

  return (
    <div className={styles.root}>
      {groups.map(group => (
        <div className={styles.group} key={group.key}>
          <span className={styles.label} id={`lease-filter-${group.key}`}>
            {group.label}
          </span>
          <ul className={styles.pills} aria-labelledby={`lease-filter-${group.key}`}>
            {group.pills.map(pill => (
              <li key={pill.key}>
                <button
                  type="button"
                  aria-pressed={pill.isActive}
                  className={cx(styles.pill, { [styles.active]: pill.isActive })}
                  onClick={() => togglePill(group.key, pill.key)}
                >
                  {pill.label}
                  <span className={styles.count}>{pill.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export default subscribe(
  [PortfoliosProvider, SearchFilterProvider],
  ({ filters }: any, { activeFilterIndex }: any) => ({
    filters,
    activeFilterIndex,
  }),
)(LeaseFilterPills)
